/**
 * Opus Message - Individual message display
 */

import { User, Bot } from 'lucide-react';
import { OpusToolActivity } from './OpusToolActivity';
import type { OpusMessage as MessageType } from '@/stores/opus-ai-store';

interface OpusMessageProps {
  message: MessageType;
}

export function OpusMessage({ message }: OpusMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div
        className={`
          flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
          ${isUser ? 'bg-emerald-500/20 text-emerald-500' : 'bg-bg-elevated text-emerald-400'}
        `}
      >
        {isUser ? (
          <User className="w-4 h-4" />
        ) : (
          <Bot className="w-4 h-4" />
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 min-w-0 ${isUser ? 'text-right' : ''}`}>
        <div
          className={`
            inline-block max-w-full text-sm rounded-xl px-4 py-2.5
            ${isUser
              ? 'bg-emerald-500/20 text-text-primary rounded-tr-sm'
              : 'bg-bg-elevated text-text-secondary rounded-tl-sm'
            }
            ${message.isStreaming ? 'animate-pulse' : ''}
          `}
        >
          {/* Message content with markdown-like formatting */}
          <div className="whitespace-pre-wrap break-words prose prose-invert prose-sm max-w-none">
            {formatContent(message.content)}
          </div>
        </div>

        {/* Tool activity (assistant messages only) */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-2">
            <OpusToolActivity toolCalls={message.toolCalls} />
          </div>
        )}

        {/* Timestamp */}
        <div className={`mt-1 text-xs text-text-tertiary/60 ${isUser ? 'text-right' : ''}`}>
          {formatTime(message.createdAt)}
        </div>
      </div>
    </div>
  );
}

function formatContent(content: string): string {
  // Basic formatting - keep newlines and trim
  return content.trim();
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
